import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { authService } from '../../services/auth.service';
import { trainingsService, TrainingStats } from '../../services/trainings.service';
import StatCard from '../../components/dashboard/StatCard';
import { 
  BookOpen, 
  Users, 
  Calendar, 
  TrendingUp, 
  DollarSign, 
  FileText,
  Plus,
  Eye,
  Edit,
  MoreHorizontal
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface DashboardStats {
  totalTrainings: number;
  activeTrainings: number;
  totalSessions: number;
  totalSubscriptions: number;
  totalRevenue: number;
  pendingInvoices: number;
}

interface RecentTraining {
  id: string;
  title: string;
  status: string;
  participants: number;
  startDate: string;
}

interface TrainingCenterInfo {
  id: number;
  name: string;
  phone_number: string;
  about?: string;
  address?: string;
  website?: string;
  social_media_links?: string;
  field_ids?: number[];
  wilaya_id?: number;
}

const TrainingCenterDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [trainingCenter, setTrainingCenter] = useState<TrainingCenterInfo | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalTrainings: 0,
    activeTrainings: 0,
    totalSessions: 0,
    totalSubscriptions: 0,
    totalRevenue: 0,
    pendingInvoices: 0
  });
  const [trainingStats, setTrainingStats] = useState<TrainingStats>({
    total: 0,
    active: 0,
    completed: 0
  });
  const [recentTrainings, setRecentTrainings] = useState<RecentTraining[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        // Fetch training center info
        const trainingCenterInfo = await authService.getTrainingCenterInfo();
        setTrainingCenter(trainingCenterInfo);

        // Load training statistics
        const trainingStatsData = await trainingsService.getTrainingStats();
        setTrainingStats(trainingStatsData);
        
        // Load dashboard statistics with real data
        const dashboardStatsData = await trainingsService.getDashboardStats();
        setStats(dashboardStatsData);

        // Load recent trainings
        const trainingsData = await trainingsService.getAllTrainings();
        const recentTrainingsData = trainingsData.slice(0, 5).map(training => ({
          id: training.id,
          title: training.title,
          status: training.schedule_date && !isNaN(new Date(training.schedule_date).getTime())
            ? (new Date(training.schedule_date) > new Date() ? t('trainingCenter.dashboard.status.upcoming') : t('trainingCenter.dashboard.status.active'))
            : t('trainingCenter.dashboard.status.upcoming'),
          participants: (training as any).participant_count || 0, // Use real participant count if available
          startDate: training.schedule_date
        }));
        setRecentTrainings(recentTrainingsData);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-brand-primary/5 via-brand-secondary/5 to-brand-mint/5 rounded-xl p-4 sm:p-6 border border-brand-primary/20">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                {t('trainingCenter.dashboard.welcome', { name: trainingCenter?.name || 'Centre de Formation' })}
              </h2>
              <p className="text-sm sm:text-base text-gray-600">
                {t('trainingCenter.dashboard.subtitle')}
              </p>
            </div>
            <Link
              to="/training-center/trainings/create"
              className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-brand-primary to-brand-secondary text-white font-medium rounded-lg hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all shadow-md text-sm sm:text-base"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('trainingCenter.dashboard.newTraining')}
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 sm:gap-6">
          <StatCard
            title={t('trainingCenter.dashboard.stats.totalTrainings')}
            value={stats.totalTrainings}
            icon={BookOpen}
            color="brand-primary"
          />
          <StatCard
            title={t('trainingCenter.dashboard.stats.activeTrainings')}
            value={stats.activeTrainings}
            icon={Calendar}
            color="brand-secondary"
          />
          <StatCard
            title={t('trainingCenter.dashboard.stats.totalSessions')}
            value={stats.totalSessions}
            icon={Users}
            color="brand-primary"
          />
          <StatCard
            title={t('trainingCenter.dashboard.stats.subscriptions')}
            value={stats.totalSubscriptions}
            icon={TrendingUp}
            color="brand-accent"
          />
          <StatCard
            title={t('trainingCenter.dashboard.stats.revenue')}
            value={`${stats.totalRevenue.toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')} DA`}
            icon={DollarSign}
            color="brand-mint"
          />
          <StatCard
            title={t('trainingCenter.dashboard.stats.pendingInvoices')}
            value={stats.pendingInvoices}
            icon={FileText}
            color="red"
          />
        </div>

        {/* Recent Trainings */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">{t('trainingCenter.dashboard.recentTrainings')}</h3>
              <Link
                to="/training-center/trainings"
                className="text-sm font-medium text-brand-primary hover:text-brand-primary/80 self-start sm:self-auto"
              >
                {t('trainingCenter.dashboard.viewAll')}
              </Link>
            </div>
          </div>
          <div className="p-4 sm:p-6">
            {recentTrainings.length > 0 ? (
              <div className="space-y-4">
                {recentTrainings.map((training) => (
                  <div
                    key={training.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">{training.title}</h4>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            training.status === t('trainingCenter.dashboard.status.active')
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {training.status}
                          </span>
                          <span>{training.participants} {t('trainingCenter.dashboard.participants')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="hidden sm:inline">
                            {training.startDate && !isNaN(new Date(training.startDate).getTime())
                              ? new Date(training.startDate).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')
                              : t('trainingCenter.trainings.messages.noSession')}
                          </span>
                          <span className="sm:hidden">
                            {training.startDate && !isNaN(new Date(training.startDate).getTime())
                              ? new Date(training.startDate).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { day: '2-digit', month: '2-digit', year: '2-digit' })
                              : t('trainingCenter.trainings.messages.noSession')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                      <Link
                        to={`/training-center/trainings/${training.id}`}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        title={t('trainingCenter.dashboard.actions.view')}
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                      <Link
                        to={`/training-center/trainings/${training.id}/edit`}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        title={t('trainingCenter.dashboard.actions.edit')}
                      >
                        <Edit className="h-4 w-4" />
                      </Link>
                      <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors" title={t('trainingCenter.dashboard.actions.moreOptions')}>
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('trainingCenter.dashboard.emptyState.title')}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {t('trainingCenter.dashboard.emptyState.subtitle')}
                </p>
                <div className="mt-6">
                  <Link
                    to="/training-center/trainings/create"
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-brand-primary to-brand-secondary text-white font-medium rounded-lg hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('trainingCenter.dashboard.emptyState.createTraining')}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/training-center/trainings/create"
            className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-center"
          >
            <Plus className="h-8 w-8 text-brand-primary mx-auto mb-2" />
            <h4 className="font-medium text-gray-900">{t('trainingCenter.dashboard.quickActions.newTraining')}</h4>
            <p className="text-sm text-gray-500">{t('trainingCenter.dashboard.quickActions.createTraining')}</p>
          </Link>
          <Link
            to="/training-center/participants"
            className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-center"
          >
            <Users className="h-8 w-8 text-brand-secondary mx-auto mb-2" />
            <h4 className="font-medium text-gray-900">{t('trainingCenter.dashboard.quickActions.participants')}</h4>
            <p className="text-sm text-gray-500">{t('trainingCenter.dashboard.quickActions.manageSubscriptions')}</p>
          </Link>
          <Link
            to="/training-center/stats"
            className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-center"
          >
            <TrendingUp className="h-8 w-8 text-brand-primary mx-auto mb-2" />
            <h4 className="font-medium text-gray-900">{t('trainingCenter.dashboard.quickActions.statistics')}</h4>
            <p className="text-sm text-gray-500">{t('trainingCenter.dashboard.quickActions.viewAnalytics')}</p>
          </Link>
          <Link
            to="/training-center/profile"
            className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-center"
          >
            <Edit className="h-8 w-8 text-brand-accent mx-auto mb-2" />
            <h4 className="font-medium text-gray-900">{t('trainingCenter.dashboard.quickActions.profile')}</h4>
            <p className="text-sm text-gray-500">{t('trainingCenter.dashboard.quickActions.editProfile')}</p>
          </Link>
        </div>
      </div>
  );
};

export default TrainingCenterDashboardPage; 